/* eslint-disable unused-imports/no-unused-vars, @typescript-eslint/no-empty-function */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type CollectionItemType from '~types/CollectionItemType';
import type AggregateTraitType from '~types/token/AggregateTraitType';

// eslint-disable-next-line absolutely-import/import
import { COLLECTION_PAGE_SIZE } from './collection-items.consts';

type CollectionUploadState = {
  tokens: { [name: string]: CollectionItemType };
  traits: Array<AggregateTraitType>;
  tokensOrder: Array<string>;
  pageLoaded: number;
  isPageLoading: boolean;
  isNextPage: boolean;
  selection: { [name: string]: string };
};

const initialState: CollectionUploadState = {
  tokens: {},
  traits: [],
  tokensOrder: [],
  pageLoaded: 0,
  isPageLoading: false,
  isNextPage: true,
  selection: {},
};

const collectionItemsSlice = createSlice({
  name: 'collection-items',
  initialState,
  reducers: {
    clearCollection(state) {
      state.tokens = initialState.tokens;
      state.traits = initialState.traits;
      state.tokensOrder = initialState.tokensOrder;
      state.pageLoaded = initialState.pageLoaded;
      state.selection = initialState.selection;
      state.isPageLoading = initialState.isPageLoading;
      state.isNextPage = initialState.isNextPage;
    },
    getCollection(state, action: PayloadAction<{ page: string }>) {
      state.isPageLoading = true;
    },
    getCollectionNextPage(state) {},
    getCollectionSuccess(state, action: PayloadAction<Array<CollectionItemType>>) {
      const tokensOrder = [];
      const tokens = action.payload.reduce((acc, curr: CollectionItemType) => {
        tokensOrder.push(curr.name);
        return {
          ...acc,
          [curr.name?.toString()]: curr,
        };
      }, {});
      state.pageLoaded += 1;
      state.isPageLoading = false;
      if (action.payload.length !== COLLECTION_PAGE_SIZE) {
        state.isNextPage = false;
      }
      state.tokens = {
        ...state.tokens,
        ...tokens,
      };
      state.tokensOrder = [...state.tokensOrder, ...tokensOrder];
    },
    getCollectionFailed(state) {},
    addCollectionItem(state, action: PayloadAction<CollectionItemType>) {
      const { name } = action.payload;
      state.tokens[name] = action.payload;
      state.tokensOrder.push(name);
    },
    deleteCollectionItem(state, action: PayloadAction<CollectionItemType>) {},
    deleteCollectionItemSuccess(state, action: PayloadAction<CollectionItemType>) {
      const { name } = action.payload;
      state.tokens[name] = undefined;
      state.tokensOrder = state.tokensOrder.filter((item: string) => item !== name);
    },
    deleteCollectionItemError(state, action: PayloadAction<CollectionItemType>) {},
    deleteSelectedCollectionItems(state) {},
    deleteSelectedCollectionItemsSuccess(state, action: PayloadAction<{ [name: string]: string }>) {
      const names = Object.values(action.payload);

      const newTokens = state.tokens;
      names.forEach((item) => {
        delete newTokens[item];
      });
      state.tokens = newTokens;
      state.tokensOrder = state.tokensOrder.filter((item: string) => !names.includes(item));
      state.selection = initialState.selection;
    },
    deleteSelectedCollectionItemsError(state) {},
    updateCollectionItem(state, action: PayloadAction<CollectionItemType>) {},
    updateCollectionItemSuccess(state, action: PayloadAction<CollectionItemType>) {
      const { name } = action.payload;
      state.tokens[name] = action.payload;
    },
    updateCollectionItemError(state, action: PayloadAction<string>) {},
    selectCollectionItem(state, action: PayloadAction<CollectionItemType>) {
      const token = action.payload;
      state.selection[token._id] = token.name;
    },
    unselectCollectionItem(state, action: PayloadAction<CollectionItemType>) {
      const token = action.payload;
      delete state.selection[token._id];
    },
    setCollectionTraits(state, action: PayloadAction<Array<AggregateTraitType>>) {
      state.traits = [...action.payload];
    },
    getCollectionTraits(state) {},
    updateCollectionTraits(state, action: PayloadAction<CollectionItemType>) {},
  },
});

export const {
  addCollectionItem,
  clearCollection,
  deleteCollectionItem,
  deleteCollectionItemError,
  deleteCollectionItemSuccess,
  deleteSelectedCollectionItems,
  deleteSelectedCollectionItemsError,
  deleteSelectedCollectionItemsSuccess,
  getCollection,
  getCollectionFailed,
  getCollectionNextPage,
  getCollectionSuccess,
  selectCollectionItem,
  unselectCollectionItem,
  updateCollectionItem,
  updateCollectionItemError,
  updateCollectionItemSuccess,
  setCollectionTraits,
  getCollectionTraits,
  updateCollectionTraits,
} = collectionItemsSlice.actions;
export default collectionItemsSlice.reducer;
